/**
 * RIFT — systems / settings.
 *
 * Opened from the home screen's SETTINGS nav item. Deliberately NOT a second
 * settings implementation: every control here reads and writes the exact same
 * `ctx.config` fields, the exact same localStorage keys, and emits the exact
 * same `ui:*` events as the in-match pause menu (`src/ui/menu.js`), so a
 * choice made before a deployment and a choice made mid-match can never
 * disagree. Only the chrome is different — this screen is reached before
 * `render`/`player` exist yet (the home screen shows before `engine.init()`
 * resolves), so every live-system touch is optional-chained exactly like the
 * rest of mainmenu.js already does.
 *
 *     const settings = new Settings(host, ctx, { onClose: () => {} });
 *     settings.show();
 */
import { QUALITY_ORDER } from '../core/config.js';
import { QUALITY_KEY } from './menu.js';
import { IDLE_KEY, IDLE_MODES } from '../core/idle.js';
import { clamp } from './util.js';
import { HUD_TOKENS, HUD_CSS, mountCorners } from './theme.js';

const STYLE_ID = 'rs-settings-style';

export class Settings {
  constructor(host, ctx, opts = {}) {
    this.host = host || document.body;
    this.ctx = ctx || null;
    this.onClose = opts.onClose ?? null;
    this.visible = false;

    this._injectStyle();
    this._build();
    this.syncFromConfig();
  }

  _el(tag, cls, parent, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    if (parent) parent.appendChild(n);
    return n;
  }

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  _build() {
    this.root = this._el('section', 'rs-root', null);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Systems');
    this.root.hidden = true;
    mountCorners(this.root);

    const head = this._el('div', 'rs-head', this.root);
    const headL = this._el('div', null, head);
    this._el('div', 'hud-eyebrow', headL, 'SYSTEMS');
    this._el('h1', 'rs-title hud-title', headL, 'SYSTEM CONFIGURATION');
    this._el('p', 'rs-copy', headL, 'Applies immediately and carries into the match, in the pause menu and here alike.');
    const close = this._el('button', 'hud-close rs-headclose', head, 'CLOSE');
    close.type = 'button';
    close.addEventListener('click', () => this._close());

    this.rows = this._el('div', 'rs-rows', this.root);

    // ---- quality preset ---------------------------------------------------
    this.qBtns = [];
    const qRow = this._row('Graphics Preset');
    const qSeg = this._el('div', 'rs-seg', qRow);
    for (const p of QUALITY_ORDER) {
      const b = this._el('button', 'hud-btn rs-seg-btn', qSeg, p.toUpperCase());
      b.type = 'button';
      b.addEventListener('click', () => this.setQuality(p));
      this.qBtns.push(b);
    }
    this.qNote = this._el('div', 'rs-hint', this.rows, '');

    // ---- sensitivity --------------------------------------------------------
    this.sens = this._slider('Mouse Sensitivity', 0.2, 3.0, 0.01, (v) => {
      if (!this.ctx?.config) return v.toFixed(2);
      this.ctx.config.sensitivity = 0.0022 * v;
      this.ctx.events?.emit('ui:sensitivity', { value: this.ctx.config.sensitivity, multiplier: v });
      return v.toFixed(2);
    });

    // ---- field of view --------------------------------------------------------
    this.fov = this._slider('Field Of View', 65, 120, 1, (v) => {
      if (!this.ctx?.config) return String(v | 0);
      this.ctx.config.fov = v;
      const cam = this.ctx.camera;
      if (cam) {
        cam.fov = v;
        cam.updateProjectionMatrix();
      }
      this.ctx.events?.emit('ui:fov', { value: v });
      return String(v | 0);
    });

    // ---- invert look --------------------------------------------------------
    const invRow = this._row('Invert Look');
    const invSeg = this._el('div', 'rs-seg', invRow);
    this.invBtns = [];
    for (const [label, val] of [
      ['OFF', false],
      ['ON', true],
    ]) {
      const b = this._el('button', 'hud-btn rs-seg-btn', invSeg, label);
      b.type = 'button';
      b.addEventListener('click', () => {
        if (!this.ctx?.config) return;
        this.ctx.config.invertY = val;
        this.ctx.events?.emit('ui:setting', { key: 'invertY', value: val });
        this.syncFromConfig();
      });
      this.invBtns.push([b, val]);
    }

    // ---- idle governor --------------------------------------------------------
    const idleRow = this._row('When Not Playing');
    const idleSeg = this._el('div', 'rs-seg', idleRow);
    this.idleBtns = [];
    for (const [label, mode] of [
      ['FULL', 'off'],
      ['IDLE', 'safe'],
      ['BG', 'background'],
    ]) {
      const b = this._el('button', 'hud-btn rs-seg-btn', idleSeg, label);
      b.type = 'button';
      b.addEventListener('click', () => this.setIdleMode(mode));
      this.idleBtns.push([b, mode]);
    }
    this.idleNote = this._el('div', 'rs-hint', this.rows, '');

    const foot = this._el('div', 'rs-foot', this.root);
    this.flash = this._el('div', 'rs-flash', foot, '');
    const reset = this._el('button', 'hud-btn', foot, 'DEFAULTS');
    reset.type = 'button';
    reset.addEventListener('click', () => this._resetDefaults());
    const closeBtn = this._el('button', 'hud-btn hud-btn-primary rs-close', foot, 'CLOSE');
    closeBtn.type = 'button';
    closeBtn.addEventListener('click', () => this._close());

    this.host.appendChild(this.root);
  }

  _row(name) {
    const r = this._el('div', 'rs-row', this.rows);
    this._el('div', 'rs-row-name', r, name.toUpperCase());
    return r;
  }

  _slider(name, min, max, step, apply) {
    const row = this._row(name);
    const wrap = this._el('div', 'rs-slider', row);
    const track = this._el('div', 'hud-bar rs-slider-track', wrap);
    const fillBar = this._el('i', null, track);
    const input = this._el('input', 'rs-slider-input', wrap);
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    const val = this._el('div', 'rs-slider-val', row, '');

    const paint = (v) => {
      const t = (v - min) / (max - min);
      fillBar.style.width = (t * 100).toFixed(2) + '%';
      val.textContent = apply(v) ?? String(v);
    };
    input.addEventListener('input', () => paint(parseFloat(input.value)));
    return {
      set: (v) => {
        const c = clamp(v, min, max);
        input.value = String(c);
        paint(c);
      },
    };
  }

  /** See PauseMenu.setQuality (`src/ui/menu.js`) — identical logic, same key. */
  setQuality(name) {
    try {
      this.ctx?.config?.setQuality?.(name);
      globalThis.localStorage?.setItem(QUALITY_KEY, name);
      const render = this.ctx?.peek?.('render');
      if (render) {
        const snapped = render.governor?.reset(this.ctx.config.q.renderScale);
        render.setRenderScale(snapped ?? this.ctx.config.q.renderScale);
      }
      this.ctx?.events?.emit('ui:quality', { quality: name });
    } catch (err) {
      console.warn('[settings] quality switch failed', err);
    }
    this.syncFromConfig();
    this.qNote.textContent = 'RESOLUTION APPLIED NOW · FULL PRESET ON NEXT LAUNCH';
  }

  /** See PauseMenu.setIdleMode. */
  setIdleMode(mode) {
    if (!IDLE_MODES.includes(mode) || !this.ctx?.config) return;
    this.ctx.config.idleMode = mode;
    try {
      globalThis.localStorage?.setItem(IDLE_KEY, mode);
    } catch {
      /* private mode / storage disabled */
    }
    this.ctx.engine?.idle?.wake();
    this.ctx.events?.emit('ui:setting', { key: 'idleMode', value: mode });
    this.syncFromConfig();
  }

  _resetDefaults() {
    this.sens.set(1);
    this.fov.set(80);
    if (this.ctx?.config) this.ctx.config.invertY = false;
    this.setIdleMode('safe');
    this.setQuality('ultra');
    this._say('RESTORED DEFAULTS');
  }

  syncFromConfig() {
    const cfg = this.ctx?.config;
    if (!cfg) return;
    for (let i = 0; i < this.qBtns.length; i++) this.qBtns[i].classList.toggle('hud-on', QUALITY_ORDER[i] === cfg.quality);
    const render = this.ctx?.peek?.('render');
    const pct = render?.governor ? ` · RENDERING AT ${(render.governor.scale * 100) | 0}%` : '';
    this.qNote.textContent = (cfg.autoQuality ? `AUTO-DETECTED${cfg.gpu ? ' · ' + cfg.gpu : ''}` : 'MANUAL') + pct;

    for (const [b, v] of this.invBtns ?? []) b.classList.toggle('hud-on', !!cfg.invertY === v);

    const idle = cfg.idleMode ?? 'safe';
    for (const [b, m] of this.idleBtns ?? []) b.classList.toggle('hud-on', idle === m);
    this.idleNote.textContent =
      {
        off: 'ALWAYS RENDERING · EVEN PAUSED OR HIDDEN',
        safe: 'PAUSED AND HIDDEN FRAMES SKIPPED · NOTHING VISIBLE CHANGES',
        background: 'AS IDLE, PLUS 10 FPS WHILE THIS WINDOW IS NOT FOCUSED',
      }[idle] ?? '';

    this.sens?.set((cfg.sensitivity ?? 0.0022) / 0.0022);
    this.fov?.set(cfg.fov ?? 80);
  }

  _say(msg) {
    this.flash.textContent = msg;
    this.flash.classList.remove('rs-flash-on');
    void this.flash.offsetWidth;
    this.flash.classList.add('rs-flash-on');
  }

  _close() {
    this.hide();
    this.onClose?.();
  }

  show() {
    if (this.visible) return;
    this.visible = true;
    this.syncFromConfig();
    this.root.hidden = false;
    void this.root.offsetWidth;
    this.root.classList.add('rs-live');
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.remove('rs-live');
    this.root.hidden = true;
  }

  dispose() {
    this.hide();
    try {
      this.root?.remove();
    } catch {
      /* already gone */
    }
  }
}

const CSS = `
/* The display:flex below outranks the UA's [hidden]{display:none}, so without
   this closing the screen leaves a transparent z-index:60 sheet across the
   viewport that silently eats every click on the menu underneath. */
.rs-root[hidden]{display:none!important}
.rs-root {
  ${HUD_TOKENS}
  position:fixed; inset:0; z-index:60; display:flex; flex-direction:column;
  gap:clamp(10px,1.6vh,20px); padding:clamp(16px,3vh,44px) clamp(18px,4vw,64px);
  background:
    radial-gradient(60% 55% at 18% 16%, rgba(121,210,255,.16) 0%, transparent 60%),
    radial-gradient(55% 60% at 86% 88%, rgba(255,176,42,.18) 0%, transparent 65%),
    radial-gradient(140% 100% at 50% 50%, rgba(22,32,42,.92) 0%, rgba(4,6,9,.99) 70%),
    linear-gradient(180deg, #0a0e14 0%, #04060a 100%);
  color:var(--ink); font-family:var(--ff); text-transform:uppercase; letter-spacing:.05em;
  opacity:0; transition:opacity .22s ease; overflow:hidden;
}
.rs-root.rs-live { opacity:1; }
.rs-root > * { position:relative; z-index:1; }
${HUD_CSS}

.rs-head { display:flex; align-items:flex-start; justify-content:space-between; gap:28px; flex:0 0 auto;
  padding:24px 30px;
  background: linear-gradient(90deg, rgba(18,26,34,.96) 0%, rgba(10,16,21,.85) 60%, rgba(8,13,17,.55) 100%);
  border:1px solid rgba(190,214,230,.3); clip-path: var(--shear);
  position:relative; box-shadow: 0 14px 44px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.08); }
.rs-head::before { content:''; position:absolute; left:0; top:0; right:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 30%, var(--hair) 70%, transparent 95%); }
.rs-head::after { content:''; position:absolute; left:0; top:14px; bottom:14px; width:5px;
  background: linear-gradient(180deg, transparent, var(--amber) 25%, var(--amber-2) 75%, transparent);
  box-shadow: 0 0 20px rgba(255,176,42,.78), 0 0 40px rgba(255,176,42,.3); }
.rs-head-eyebrow { display:block; color:var(--amber); font-family:var(--fm); font-size:9.5px; letter-spacing:.34em;
  text-shadow: 0 0 12px rgba(255,176,42,.7), 0 0 24px rgba(255,176,42,.32); margin-bottom:8px; }
.rs-title { margin:.05em 0 .35em; font-size:clamp(32px,3.8vw,54px); line-height:1; }
.rs-copy { margin:6px 0 0; max-width:64ch; color:var(--ink-2); font-size:12.5px; letter-spacing:.04em;
  text-transform:none; line-height:1.6; }
.rs-headclose { margin-top:6px; }

.rs-rows { flex:1 1 auto; display:flex; flex-direction:column; gap:10px; max-width:820px; overflow:auto; padding:10px 4px 4px; }
.rs-row { display:grid; grid-template-columns:210px 1fr auto; align-items:center; gap:20px;
  padding:18px 24px 18px 26px;
  border:1px solid rgba(190,214,230,.26);
  background: linear-gradient(90deg, rgba(16,24,32,.92) 0%, rgba(8,13,17,.75) 100%);
  clip-path: var(--shear-sm);
  transition: all .2s ease; position:relative; overflow:hidden;
  box-shadow: 0 6px 22px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.05); }
.rs-row::before { content:''; position:absolute; left:0; top:0; right:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 50%, transparent 95%); }
.rs-row::after { content:''; position:absolute; left:0; top:14px; bottom:14px; width:3px;
  background: linear-gradient(180deg, transparent, var(--amber), transparent);
  opacity:.55; transition: opacity .2s ease; }
.rs-row:hover { border-color: rgba(255,176,42,.6);
  background: linear-gradient(90deg, rgba(22,30,40,.95), rgba(12,18,24,.8));
  transform: translateX(3px);
  box-shadow: 0 8px 26px rgba(0,0,0,.55), 0 0 22px rgba(255,176,42,.12); }
.rs-row:hover::after { opacity:1; box-shadow: 0 0 12px rgba(255,176,42,.55); }
.rs-row-name { font-size:11.5px; letter-spacing:.2em; color:#fff; font-weight:700;
  text-shadow: 0 0 10px rgba(0,0,0,.6), 0 0 18px rgba(255,255,255,.06);
  display:flex; align-items:center; gap:10px; }
.rs-row-name::before { content:''; width:5px; height:5px; background: var(--amber); border-radius:50%;
  box-shadow: 0 0 10px var(--amber), 0 0 18px rgba(255,176,42,.45); }
.rs-seg { grid-column:2 / span 2; display:flex; gap:6px; flex-wrap:wrap; }
.rs-seg-btn { padding:11px 22px; font-weight:800; }
.rs-seg-btn:hover { transform: translateY(-1px); }
.rs-hint { margin:6px 0 0; font-family:var(--fm); font-size:10px; letter-spacing:.1em;
  color:var(--ink-2); text-transform:none; grid-column:1 / -1;
  padding:10px 14px 10px 18px; border-left:3px solid var(--cyan);
  background: linear-gradient(90deg, rgba(38,106,135,.18), rgba(38,106,135,.05));
  position:relative; box-shadow: inset 0 1px 0 rgba(121,210,255,.1); }
.rs-hint::before { content:'▸'; color:var(--cyan); text-shadow: 0 0 8px var(--cyan); margin-right:10px; font-size:11px; }

.rs-slider { position:relative; display:flex; align-items:center; padding:0 4px; }
.rs-slider-track { flex:1; height:10px;
  background:
    linear-gradient(180deg, rgba(4,8,12,.95), rgba(10,16,21,.9));
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.6), inset 0 2px 4px rgba(0,0,0,.5), 0 0 0 1px rgba(190,214,230,.16);
  border-radius:1px; overflow:hidden; position:relative; }
.rs-slider-track::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.12), transparent); }
.rs-slider-track > i { transition:width .05s linear;
  background: linear-gradient(180deg, var(--amber-3) 0%, var(--amber) 50%, var(--amber-2) 100%);
  box-shadow: 0 0 14px rgba(255,176,42,.75), inset 0 1px 0 rgba(255,255,255,.45), inset 0 -1px 0 rgba(0,0,0,.3);
  position:relative; }
.rs-slider-track > i::after { content:''; position:absolute; right:0; top:0; bottom:0; width:14px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.7));
  box-shadow: 0 0 8px rgba(255,255,255,.4); }
.rs-slider-input { position:absolute; inset:-10px 0; width:100%; height:28px; margin:0;
  opacity:0; cursor:pointer; }
.rs-slider-val { font-family:var(--fd); font-size:16px; color:var(--amber-3);
  text-align:right; min-width:3.8em; font-weight:800;
  text-shadow: 0 0 12px rgba(255,176,42,.6), 0 0 24px rgba(255,176,42,.25), 0 1px 0 rgba(0,0,0,.5); }

.rs-foot { display:flex; align-items:center; gap:16px; flex:0 0 auto;
  padding:14px 22px; background:linear-gradient(90deg, rgba(12,18,24,.92), rgba(8,13,17,.7));
  border:1px solid rgba(190,214,230,.26);
  clip-path: var(--shear-sm);
  position:relative; }
.rs-foot::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(190,214,230,.4), transparent); }
.rs-flash { font-family:var(--fm); font-size:10px; letter-spacing:.18em; color:var(--amber-3); opacity:0; transition:opacity .2s ease;
  text-shadow: 0 0 12px rgba(255,176,42,.6); display:flex; align-items:center; gap:8px; }
.rs-flash::before { content:''; width:18px; height:1px; background:var(--amber); box-shadow: 0 0 8px var(--amber); }
.rs-flash-on { opacity:1; }
.rs-close { margin-left:auto; padding:12px 34px; font-weight:800; }
`;
